import React, { useState, useMemo } from 'react';
import '../../../../styles/contacts.css';
import { TabBar } from '../../../common/TabBar';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import {
  HiViewGrid,
  HiMail,
  HiPhone,
  HiGlobe,
  HiHome,
  HiPencil,
  HiChat,
} from 'react-icons/hi';
import { useFlywheelEnabled } from '../../../../stores/useOrganizationStore';
import { ActivityTab } from './ActivityTab';
import { EmailsTab } from './EmailsTab';
import { CallsTab } from './CallsTab';
import { SmsTab } from './SmsTab';
// TODO: Uncomment when ready
// import { ScheduledCallsTab } from './ScheduledCallsTab';
import { WebsiteActivityTab } from './WebsiteActivityTab';
import { NotesTab } from './NotesTab';
import { Contact } from '../../../../types/contact.types';
import type { MappedContact } from '../../../../hooks/graphql/contacts/useContact';
import { HomeownerDataPanel } from '../remi/HomeownerDataPanel';

interface ContactActivityTabsProps {
  contactId: string;
  firebaseUid?: string;
  contactFirstName?: string;
  contact?: Contact | null;
  defaultTab?:
    | 'activity'
    | 'notes'
    | 'emails'
    | 'website'
    | 'calls'
    | 'sms'
    | 'follow_up'
    | 'scheduled_calls'
    | 'saved_properties'
    | 'home_valuations'
    | 'saved_search'
    | 'property_insights';
  initialDateRange?: { from: Date | null; to: Date | null };
  onContactUpdate?: () => void | Promise<void>;
  /** Fires whenever the user switches tabs — lets the parent layout
   *  size differently depending on which tab is showing. */
  onTabChange?: (tab: string) => void;
}

type TabValue =
  | 'activity'
  | 'notes'
  | 'emails'
  | 'website'
  | 'calls'
  | 'sms'
  | 'follow_up'
  | 'scheduled_calls'
  | 'saved_properties'
  | 'home_valuations'
  | 'saved_search'
  | 'property_insights';

export const ContactActivityTabs: React.FC<ContactActivityTabsProps> = ({
  contactId,
  firebaseUid,
  contactFirstName,
  contact,
  defaultTab = 'activity',
  initialDateRange,
  onContactUpdate,
  onTabChange,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabValue>(defaultTab);
  const flywheelEnabled = useFlywheelEnabled();

  // Cast contact to MappedContact at runtime to access GraphQL nested data
  const mappedContact = contact as MappedContact | null | undefined;
  const propertyEnrichmentItem = mappedContact?.propertyEnrichment?.items?.[0] ?? null;

  // Create a 90-day date range for the activity tab based on the end date
  const activityDateRange = useMemo(() => {
    const endDate = initialDateRange?.to || new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 90);

    return {
      from: startDate,
      to: endDate,
    };
  }, [initialDateRange]);

  // Only the Notes tab shows a count — it's the one surface where the total
  // is reliably on the GET_CONTACT response. Other tabs lazy-load their data
  // (calls/emails are separate queries now), so a count here would be wrong.
  const notesCount = useMemo(() => {
    const c = mappedContact as unknown as Record<string, { items?: unknown[]; total?: number } | undefined>;
    return c?.notes?.total ?? c?.notes?.items?.length;
  }, [mappedContact]);

  // Tab list — labels and order match the Remi Activity design verbatim.
  // Notes sits between "Communication so far" and Calls (its original
  // position); Remi strategies only shows when flywheel is enabled; Website
  // activity folds Website + Saved properties + Home valuations under
  // inner sub-tabs.
  const tabs = useMemo(() => {
    return [
      {
        value: 'activity',
        label: 'Communication so far',
        icon: <HiViewGrid className="w-4 h-4" />,
      },
      {
        value: 'notes',
        label: 'Notes',
        icon: <HiPencil className="w-4 h-4" />,
        // Hide the badge when the contact has no notes — don't display "0".
        ...(notesCount && notesCount > 0 ? { count: notesCount } : {}),
      },
      {
        value: 'calls',
        label: 'Calls',
        icon: <HiPhone className="w-4 h-4" />,
      },
      {
        value: 'sms',
        label: 'SMS',
        icon: <HiChat className="w-4 h-4" />,
      },
      {
        value: 'emails',
        label: 'Email',
        icon: <HiMail className="w-4 h-4" />,
      },
      // "Remi strategies" tab removed — the Future outreach plan card on
      // the Communication so far tab covers this surface now.
      // Homeowner data is always shown; the panel renders an empty state
      // when no enrichment exists for the contact.
      {
        value: 'property_insights',
        label: 'Homeowner data',
        icon: <HiHome className="w-4 h-4" />,
      },
      {
        value: 'website',
        label: 'Website activity',
        icon: <HiGlobe className="w-4 h-4" />,
      },
    ];
  }, [flywheelEnabled, notesCount]);

  return (
    <div className="flex flex-col flex-grow rounded-2xl border border-foreground/10 bg-white dark:bg-card shadow-md overflow-hidden h-full max-lg:min-h-[480px]">
      <TabBar
        tabs={tabs}
        value={selectedTab}
        onChange={(v) => {
          const next = v as TabValue;
          setSelectedTab(next);
          onTabChange?.(next);
        }}
        className="sticky top-0 z-10 flex-shrink-0"
      />

      <div className="flex-grow min-h-0 flex flex-col overflow-hidden p-3 md:p-4 lg:p-5">
        {selectedTab === 'activity' && (
          <ActivityTab
            contactId={contactId}
            firebaseUid={firebaseUid}
            contactFirstName={contactFirstName}
            contact={contact}
            initialDateRange={activityDateRange}
            handoffNotes={contact?.handoffNotes}
          />
        )}
        {selectedTab === 'notes' && contact && (
          <NotesTab
            contact={contact}
            onNoteAdded={onContactUpdate}
            notes={mappedContact?.notes}
          />
        )}
        {selectedTab === 'emails' && (
          <EmailsTab
            contactId={contactId}
            isActive={true}
            contactName={[contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim() || undefined}
            contactEmail={contact?.email || undefined}
          />
        )}
        {selectedTab === 'website' && (
          <WebsiteActivityTab
            contactId={contactId}
            firebaseUid={firebaseUid}
            onContactUpdate={onContactUpdate}
          />
        )}
        {selectedTab === 'calls' && (
          <CallsTab contactId={contactId} contactFirstName={contactFirstName} />
        )}
        {selectedTab === 'sms' && (
          <SmsTab contactId={contactId} contactFirstName={contactFirstName} />
        )}
        {selectedTab === 'property_insights' && (
          <div className="overflow-y-auto scrollbar-custom flex-grow">
            {propertyEnrichmentItem ? (
              <HomeownerDataPanel
                contact={contact ?? null}
                enrichmentItem={propertyEnrichmentItem}
              />
            ) : (
              <EmptyStateWithIcon message="No property data yet" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
